import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool, CognitoUser, AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import config from '../aws-config';
import { hostedUILogout } from '../utils/cognitoAuth';

const pool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const buildUser = useCallback((session) => {
    const payload = session.getIdToken().decodePayload();
    setToken(session.getIdToken().getJwtToken());
    return {
      id: payload.sub,
      email: payload.email,
      firstName: payload.given_name,
      lastName: payload.family_name,
      role: payload['custom:role'] || 'caregiver',
      groups: payload['cognito:groups'] || [],
      isAdmin: (payload['cognito:groups'] || []).includes('admins'),
    };
  }, []);

  const loadSession = useCallback(() => new Promise((resolve) => {
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) { resolve(null); return; }
    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) { resolve(null); return; }
      const u = buildUser(session);
      setUser(u);
      resolve({ session, returnPath: '/dashboard' });
    });
  }), [buildUser]);

  useEffect(() => {
    loadSession().finally(() => setLoading(false));
  }, [loadSession]);

  // Called from CallbackPage after tokens are stored in localStorage.
  const refreshSession = useCallback(async () => {
    return loadSession();
  }, [loadSession]);

  // Direct SRP login — still used by the register flow (caregivers after confirming email).
  const login = (email, password) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        setUser(buildUser(session));
        resolve();
      },
      onFailure: (err) => {
        reject(err.message || 'Login failed');
      },
      newPasswordRequired: () => {
        reject('NEW_PASSWORD_REQUIRED');
      },
    });
  });

  const logout = () => {
    setUser(null);
    setToken(null);
    hostedUILogout();
  };

  const confirmEmail = (email, code) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) reject(err.message);
      else resolve(result);
    });
  });

  const forgotPassword = (email) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err.message),
    });
  });

  const resetPassword = (email, code, newPassword) => new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err.message),
    });
  });

  const getToken = () => new Promise((resolve) => {
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) return resolve(null);
    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });

  const register = (email, password, firstName, lastName, role, inviteCode) =>
    new Promise((resolve, reject) => {
      const attributes = [
        new CognitoUserAttribute({ Name: 'email',       Value: email }),
        new CognitoUserAttribute({ Name: 'given_name',  Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'custom:role', Value: role }),
        new CognitoUserAttribute({ Name: 'custom:invite_code', Value: inviteCode }),
      ];
      pool.signUp(email, password, attributes, null, (err, result) => {
        if (err) reject(err.message || 'Registration failed');
        else resolve(result);
      });
    });

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout,
      confirmEmail, forgotPassword, resetPassword, getToken,
      register, refreshSession
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
